import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import { toast } from 'react-toastify'
import api from '../../services/api'
import {
  GET_DRIVERS,
  GET_DRIVER,
  GET_DRIVER_PROFILE,
  POST_ADD_DRIVER,
  PUT_UPDATE_DRIVER,
  DELETE_DRIVER,
  SEARCH_DRIVER_BY_LICENSE,
  SEARCH_DRIVERS
} from '../../helpers/url_helper'

// Async thunk for fetching all drivers
export const fetchDrivers = createAsyncThunk(
  'driver/fetchAll',
  async (params, { rejectWithValue }) => {
    try {
      const queryParams = new URLSearchParams(params).toString()
      const response = await api.get(`${GET_DRIVERS}${queryParams ? `?${queryParams}` : ''}`)
      return response.data.drivers
    } catch (error) {
      const message = error.response?.data?.error || 'Failed to fetch drivers'
      if (error.response?.status !== 401) toast.error(message)
      return rejectWithValue(message)
    }
  }
)

// Async thunk for fetching single driver
export const fetchDriver = createAsyncThunk(
  'driver/fetchOne',
  async (id, { rejectWithValue }) => {
    try {
      const response = await api.get(`${GET_DRIVER}/${id}`)
      return response.data.driver
    } catch (error) {
      const message = error.response?.data?.error || 'Failed to fetch driver details'
      if (error.response?.status !== 401) toast.error(message)
      return rejectWithValue(message)
    }
  }
)

// Async thunk for fetching driver profile (own or by userId)
export const fetchDriverProfile = createAsyncThunk(
  'driver/fetchProfile',
  async (userId, { rejectWithValue }) => {
    try {
      const url = userId ? `${GET_DRIVER_PROFILE}?userId=${userId}` : GET_DRIVER_PROFILE
      const response = await api.get(url)
      return response.data.driver
    } catch (error) {
      const message = error.response?.data?.error || 'Failed to fetch driver profile'
      if (error.response?.status !== 401) toast.error(message)
      return rejectWithValue(message)
    }
  }
)

// Async thunk for adding driver
export const addDriver = createAsyncThunk(
  'driver/add',
  async (driverData, { rejectWithValue }) => {
    try {
      const response = await api.post(POST_ADD_DRIVER, driverData)
      toast.success('Driver added successfully')
      return response.data.driver
    } catch (error) {
      const message = error.response?.data?.error || 
                     error.response?.data?.message || 
                     'Failed to add driver'
      toast.error(message)
      return rejectWithValue(message)
    }
  }
)

// Async thunk for updating driver
export const updateDriver = createAsyncThunk(
  'driver/update',
  async ({ driverId, driverData }, { rejectWithValue }) => {
    try {
      const payload = driverId ? { ...driverData, driverId } : driverData
      const response = await api.put(PUT_UPDATE_DRIVER, payload)
      toast.success('Driver updated successfully')
      return response.data.driver
    } catch (error) {
      const message = error.response?.data?.error || 
                     error.response?.data?.message || 
                     'Failed to update driver'
      toast.error(message)
      return rejectWithValue(message)
    }
  }
)

// Async thunk for deleting driver
export const deleteDriver = createAsyncThunk(
  'driver/delete',
  async (id, { rejectWithValue }) => {
    try {
      await api.delete(`${DELETE_DRIVER}/${id}`)
      toast.success('Driver deactivated successfully')
      return id
    } catch (error) {
      const message = error.response?.data?.error || 'Failed to delete driver'
      toast.error(message)
      return rejectWithValue(message)
    }
  }
)

// Async thunk for searching driver by license
export const searchDriverByLicense = createAsyncThunk(
  'driver/searchByLicense',
  async (licenseNumber, { rejectWithValue }) => {
    try {
      const response = await api.get(`${SEARCH_DRIVER_BY_LICENSE}/${licenseNumber}`)
      return response.data.driver
    } catch (error) {
      const message = error.response?.data?.error || 'Driver not found'
      toast.error(message)
      return rejectWithValue(message)
    }
  }
)

// Async thunk for searching drivers with query params (name, licenseNumber, isActive, page, limit)
export const searchDrivers = createAsyncThunk(
  'driver/searchDrivers',
  async (params = {}, { rejectWithValue }) => {
    try {
      const queryParams = new URLSearchParams(params).toString()
      const response = await api.get(`${SEARCH_DRIVERS}${queryParams ? `?${queryParams}` : ''}`)
      // API may return list under different keys; try common ones
      return response.data.drivers || response.data.data || response.data
    } catch (error) {
      const message = error.response?.data?.error || 'Failed to search drivers'
      if (error.response?.status !== 401) toast.error(message)
      return rejectWithValue(message)
    }
  }
)

const initialState = {
  drivers: [],
  selectedDriver: null,
  loading: false,
  error: null,
}

const driverSlice = createSlice({
  name: 'driver',
  initialState,
  reducers: {
    clearError: state => {
      state.error = null
    },
    clearSelectedDriver: state => {
      state.selectedDriver = null
    },
  },
  extraReducers: builder => {
    builder
      // Fetch All Drivers
      .addCase(fetchDrivers.pending, state => {
        state.loading = true
        state.error = null
      })
      .addCase(fetchDrivers.fulfilled, (state, action) => {
        state.loading = false
        state.drivers = action.payload
      })
      .addCase(fetchDrivers.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload
      })
      
      // Fetch Single Driver
      .addCase(fetchDriver.pending, state => {
        state.loading = true
        state.error = null
      })
      .addCase(fetchDriver.fulfilled, (state, action) => {
        state.loading = false
        state.selectedDriver = action.payload
      })
      .addCase(fetchDriver.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload
      })
      
      // Fetch Driver Profile
      .addCase(fetchDriverProfile.pending, state => {
        state.loading = true
        state.error = null
      })
      .addCase(fetchDriverProfile.fulfilled, (state, action) => {
        state.loading = false
        state.selectedDriver = action.payload
      })
      .addCase(fetchDriverProfile.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload
      })
      
      // Add Driver
      .addCase(addDriver.pending, state => {
        state.loading = true
        state.error = null
      })
      .addCase(addDriver.fulfilled, (state, action) => {
        state.loading = false
        state.drivers.push(action.payload)
      })
      .addCase(addDriver.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload
      })
      
      // Update Driver
      .addCase(updateDriver.pending, state => {
        state.loading = true
        state.error = null
      })
      .addCase(updateDriver.fulfilled, (state, action) => {
        state.loading = false
        const index = state.drivers.findIndex(driver => driver.id === action.payload.id)
        if (index !== -1) {
          state.drivers[index] = action.payload
        }
      })
      .addCase(updateDriver.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload
      })
      
      // Delete Driver
      .addCase(deleteDriver.pending, state => {
        state.loading = true
        state.error = null
      })
      .addCase(deleteDriver.fulfilled, (state, action) => {
        state.loading = false
        state.drivers = state.drivers.filter(driver => driver.id !== action.payload)
      })
      .addCase(deleteDriver.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload
      })
      
      // Search Driver by License
      .addCase(searchDriverByLicense.pending, state => {
        state.loading = true
        state.error = null
      })
      .addCase(searchDriverByLicense.fulfilled, (state, action) => {
        state.loading = false
        state.selectedDriver = action.payload
      })
      .addCase(searchDriverByLicense.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload
      })
      // Search Drivers (general search with query params)
      .addCase(searchDrivers.pending, state => {
        state.loading = true
        state.error = null
      })
      .addCase(searchDrivers.fulfilled, (state, action) => {
        state.loading = false
        state.drivers = action.payload
      })
      .addCase(searchDrivers.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload
      })
  },
})

export const { clearError, clearSelectedDriver } = driverSlice.actions
export default driverSlice.reducer
